<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { usePilotConfigStore } from 'dashboard/store/pilot/preferences';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ModelDropdown from './ModelDropdown.vue';

const props = defineProps({
  featureKey: {
    type: String,
    required: true,
  },
  isAllowed: {
    type: Boolean,
    required: true,
  },
  // 模型只读模式（显示但不允许选择）
  readonlyModel: {
    type: Boolean,
    default: false,
  },
  // 从其他功能读取模型配置
  modelSourceKey: {
    type: String,
    default: '',
  },
  // 即将发布状态（禁用开关，显示提示）
  comingSoon: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change', 'modelChange']);

const { t } = useI18n();
const pilotConfigStore = usePilotConfigStore();
const { features } = storeToRefs(pilotConfigStore);

// 如果指定了 modelSourceKey，使用它的模型配置
const effectiveModelKey = computed(
  () => props.modelSourceKey || props.featureKey
);

const availableModels = computed(() =>
  pilotConfigStore.getModelsForFeature(effectiveModelKey.value)
);

const isEnabled = ref(false);

const featureConfig = computed(() => features.value[props.featureKey]);

const hasMultipleModels = computed(() => {
  return availableModels.value && availableModels.value.length > 1;
});

// 显示模型选择器的条件：
// 1. 如果是只读模式，始终显示（展示当前使用的模型）
// 2. 否则，需要功能开启且有多个模型可选
const showModelSelector = computed(() => {
  if (props.readonlyModel) {
    return hasMultipleModels.value;
  }
  return isEnabled.value && hasMultipleModels.value;
});

const title = computed(() => {
  const key = props.featureKey.toUpperCase();
  return t(`PILOT_SETTINGS.FEATURES.${key}.TITLE`);
});

const description = computed(() => {
  const key = props.featureKey.toUpperCase();
  return t(`PILOT_SETTINGS.FEATURES.${key}.DESCRIPTION`);
});

const modelTitle = computed(() => {
  const key = props.featureKey.toUpperCase();
  return t(`PILOT_SETTINGS.FEATURES.${key}.MODEL_TITLE`);
});

const modelDescription = computed(() => {
  const key = props.featureKey.toUpperCase();
  return t(`PILOT_SETTINGS.FEATURES.${key}.MODEL_DESCRIPTION`);
});

watch(
  featureConfig,
  newConfig => {
    if (newConfig !== undefined) {
      isEnabled.value = !!newConfig.enabled;
    }
  },
  { immediate: true }
);

const toggleFeature = () => {
  emit('change', { feature: props.featureKey, enabled: isEnabled.value });
};

const handleModelChange = ({ feature, model }) => {
  emit('modelChange', { feature, model });
};
</script>

<template>
  <div
    class="p-4 rounded-xl border border-n-weak bg-n-solid-1 flex relative"
    :class="{
      'flex-col gap-3': showModelSelector,
      'items-center justify-between gap-4': !showModelSelector,
      'opacity-60 pointer-events-none': !isAllowed,
    }"
  >
    <!-- 即将发布标签 -->
    <div
      v-if="comingSoon"
      class="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-medium text-n-slate-11 bg-n-alpha-2 rounded"
    >
      即将发布
    </div>

    <div class="flex items-center justify-between gap-4 flex-1">
      <div class="flex-1 min-w-0">
        <h4 class="text-sm font-medium text-n-slate-12">{{ title }}</h4>
        <p class="text-sm text-n-slate-11 mt-0.5">{{ description }}</p>
      </div>
      <div v-if="isAllowed && !comingSoon" class="flex-shrink-0">
        <Switch v-model="isEnabled" @change="toggleFeature" />
      </div>
      <div v-else-if="comingSoon" class="flex-shrink-0">
        <Switch :model-value="false" disabled />
      </div>
    </div>
    <div
      v-if="showModelSelector && isAllowed && !comingSoon"
      class="flex gap-2 ps-8 relative before:content-[''] before:absolute before:w-0.5 before:h-1/2 before:top-0 before:start-3 before:bg-n-weak after:content-[''] after:absolute after:w-2.5 after:h-3 after:top-[calc(50%-6px)] after:start-3 after:border-b-[0.125rem] after:border-s-[0.125rem] after:rounded-es after:border-n-weak"
    >
      <div class="flex-1 min-w-0">
        <h4 class="text-sm font-medium text-n-slate-12">{{ modelTitle }}</h4>
        <p class="text-sm text-n-slate-11 mt-0.5">{{ modelDescription }}</p>
      </div>
      <div class="flex justify-end">
        <ModelDropdown
          :feature-key="effectiveModelKey"
          :readonly="readonlyModel"
          @change="handleModelChange"
        />
      </div>
    </div>
  </div>
</template>
